import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useTranslation } from 'react-i18next';
import { showResult } from 'infrastructure/hooks/utils';
import { useAppSelector } from 'store';
import { DateFormatter } from 'infrastructure/functions';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { DateFormats, ModuleType, UserType } from 'infrastructure/enums';
import {
  getCcmInteractionTimeKey,
  getRpmInteractionTimeKey,
} from 'components/organisms/interactiveTime/hooks';

import type {
  IClinicNotesResponse,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  module: ModuleType;
}

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  module: ModuleType;
} & IPartialPaginationMeta;

export interface IDeleteManualClinicNotes {
  guid: string;
  patientGuid: string;
  module: ModuleType;
  userType: UserType;
}

export const deleteManualClinicNotesFn = async (
  postData: IDeleteManualClinicNotes,
) => {
  const { guid, module, patientGuid, userType } = postData;
  if (userType !== UserType.Admin) {
    throw new Error('You do not have permission to delete comments');
  }

  let url = `core-2/ccm/patients/${patientGuid}/manual-time/delete`;
  if (module === ModuleType.RPM) {
    url = `admins/admin-panel/patients/${patientGuid}/manual-time/delete`;
  }
  const { data, error } = await dataService.createOne<void>(url, { guid });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getManualClinicNotesKey = (filter: TFilter) => [
  'getManualClinicNotes',
  filter,
];

export const MANUAL_CLINIC_NOTES_PER_PAGE = 5;

export const useDeleteManualClinicNotes = ({
  patientGuid,
  meta,
  module,
}: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [startTime, endTime] = useAppSelector<[Date, Date] | []>(
    (state) => state.ccmPatientDetails.dates,
  );

  const filter: TFilter = {
    ...meta,
    patientGuid,
    items: MANUAL_CLINIC_NOTES_PER_PAGE,
    startTime: DateFormatter({
      date: startTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    endTime: DateFormatter({
      date: endTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    module,
  };

  const queryKey = getManualClinicNotesKey(filter);

  const {
    mutate: deleteManualClinicNotes,
    mutateAsync: deleteManualClinicNotesAsync,
    isLoading,
    ...other
  } = useMutation({
    mutationFn: deleteManualClinicNotesFn,
    onSuccess(responseData, variables) {
      notificationController.success({
        message: t('labels.commentDeleted'),
      });
      queryClient.setQueryData<IClinicNotesResponse>(queryKey, (oldData) => {
        if (oldData) {
          const newData = structuredClone(oldData);
          newData.count = newData.count > 0 ? (newData.count -= 1) : 0;
          newData.items = newData.items.filter(
            (item) => item.guid !== variables.guid,
          );

          return newData;
        }
        return oldData;
      });
      const today = new Date();
      const formattedDate = DateFormatter({ date: today });
      const rpmInteractiveTime = getRpmInteractionTimeKey(
        patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(rpmInteractiveTime);
      const ccmInteractiveTime = getCcmInteractionTimeKey(
        patientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(ccmInteractiveTime);
    },
    onError() {
      notificationController.error({
        message: t('labels.commentDeleteError'),
      });
    },
  });

  return {
    deleteManualClinicNotes,
    deleteManualClinicNotesAsync,
    deleteLoading: isLoading,
    ...other,
  };
};
